import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {
    dataResult,
    errorResult,
    patchResultForCompatibilityIfRequired,
    type Result,
} from "@vesta/error-handling-remix/result";
import {
    MediaLibraryDirentStatus,
    MediaLibraryDirentType,
    mediaLibraryDirentZodValidator,
    type MediaLibraryDirent,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/mediaLibraryWorkspaceCapability/domain/MediaLibraryDirent";
import {
    coercedIntegerZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {plutoPost} from "~/utilities/ottokart/plutoClient";

/** Pluto folder/dirent responses may include rollup fields and optional path metadata. */
const mediaLibraryDirentResponseZodValidator = mediaLibraryDirentZodValidator.and(
    zod.object({
        itemCount: coercedIntegerZodValidator.optional(),
        originHitCount: coercedIntegerZodValidator.optional(),
        path: zod.string().optional(),
        totalSizeBytes: coercedIntegerZodValidator.optional(),
    }),
);

const mediaLibraryVoidResponseZodValidator = zod.union([zod.undefined(), zod.null()]);

function workspaceHeaders({
    accessToken,
    workspaceId,
}: {
    accessToken: string;
    workspaceId: Uuid;
}): Record<string, string> {
    return {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "X-Workspace-Id": workspaceId,
    };
}

export async function seedMediaLibraryRootDirent({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<void>> {
    return robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/media-library/seed-root-dirent`,
        {
            body: JSON.stringify({}),
            headers: workspaceHeaders({accessToken, workspaceId}),
            method: "POST",
        },
        mediaLibraryVoidResponseZodValidator,
    );
}

export async function getMediaLibraryChildDirents({
    accessToken,
    direntId,
    excludeFolders,
    limit,
    offset,
    intellsysBaseUrl,
    search,
    status,
    type,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    excludeFolders?: boolean;
    limit?: number;
    offset?: number;
    intellsysBaseUrl: string;
    search?: string;
    status?: MediaLibraryDirentStatus;
    type?: MediaLibraryDirentType;
    workspaceId: Uuid;
}): Promise<Result<Array<MediaLibraryDirent>>> {
    return plutoPost({
        accessToken,
        body: {
            direntId,
            excludeFolders,
            limit,
            offset,
            search,
            status,
            type,
        },
        path: "/media-library/get-child-dirents",
        intellsysBaseUrl,
        responseValidator: zod.array(mediaLibraryDirentResponseZodValidator),
        workspaceId,
    });
}

const MEDIA_LIBRARY_LIST_ALL_PAGE_SIZE = 100;

export async function listAllMediaLibraryChildDirents({
    accessToken,
    direntId,
    intellsysBaseUrl,
    status,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    status?: MediaLibraryDirentStatus;
    workspaceId: Uuid;
}): Promise<Array<MediaLibraryDirent>> {
    const allDirents: Array<MediaLibraryDirent> = [];
    let offset = 0;

    while (true) {
        const result = await getMediaLibraryChildDirents({
            accessToken: accessToken,
            direntId: direntId,
            limit: MEDIA_LIBRARY_LIST_ALL_PAGE_SIZE,
            offset: offset,
            intellsysBaseUrl: intellsysBaseUrl,
            status: status,
            workspaceId: workspaceId,
        });

        if (result.success !== true) {
            throw result.error;
        }

        allDirents.push(...result.data);

        if (result.data.length < MEDIA_LIBRARY_LIST_ALL_PAGE_SIZE) {
            break;
        }

        offset += result.data.length;
    }

    return allDirents;
}

export async function createMediaLibraryFolder({
    accessToken,
    folderName,
    parentFolderId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    folderName: string;
    parentFolderId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<MediaLibraryDirent>> {
    return plutoPost({
        accessToken,
        body: {
            folderName,
            parentFolderId,
        },
        path: "/media-library/create-folder",
        intellsysBaseUrl,
        responseValidator: mediaLibraryDirentResponseZodValidator,
        workspaceId,
    });
}

export async function getMediaLibraryDirent({
    accessToken,
    direntId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<MediaLibraryDirent | null>> {
    return plutoPost({
        accessToken,
        body: {direntId},
        path: "/media-library/get-dirent",
        intellsysBaseUrl,
        responseValidator: mediaLibraryDirentResponseZodValidator.nullable(),
        workspaceId,
    });
}

export async function getMediaLibraryDirents({
    accessToken,
    direntIds,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntIds: Array<Uuid>;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<Array<MediaLibraryDirent>>> {
    if (direntIds.length === 0) {
        return dataResult([]);
    }

    return plutoPost({
        accessToken,
        body: {direntIds},
        path: "/media-library/get-dirents",
        intellsysBaseUrl,
        responseValidator: zod.array(mediaLibraryDirentResponseZodValidator),
        workspaceId,
    });
}

export async function getMediaLibraryDirentForPath({
    accessToken,
    intellsysBaseUrl,
    path,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    path: string;
    workspaceId: Uuid;
}): Promise<Result<MediaLibraryDirent | null>> {
    return plutoPost({
        accessToken,
        body: {path},
        path: "/media-library/get-dirent-for-path",
        intellsysBaseUrl,
        responseValidator: mediaLibraryDirentResponseZodValidator.nullable(),
        workspaceId,
    });
}

async function fetchMediaLibraryDirentBlob({
    accessToken,
    direntId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Blob> {
    const response = await fetch(`${intellsysBaseUrl}/sub-system/pluto/media-library/dirent/${direntId}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-Workspace-Id": workspaceId,
        },
        method: "GET",
    });

    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json") === true) {
        const body = (await response.json()) as {error?: string; success?: boolean};
        throw new Error(body.error ?? "Failed to load media file");
    }

    if (response.ok !== true) {
        throw new Error(`Failed to load media file (${String(response.status)})`);
    }

    return response.blob();
}

export async function fetchMediaLibraryDirentPreviewBlob({
    accessToken,
    direntId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Blob> {
    return fetchMediaLibraryDirentBlob({
        accessToken,
        direntId,
        intellsysBaseUrl,
        workspaceId,
    });
}

function parseMediaLibraryUploadJsonResponse(responseBody: unknown): Result<MediaLibraryDirent> {
    const response = patchResultForCompatibilityIfRequired(
        responseBody as Parameters<typeof patchResultForCompatibilityIfRequired>[0],
    );

    if (response.success === false) {
        const errorMessage =
            "error" in response && typeof response.error === "string" && response.error !== ""
                ? response.error
                : "Upload failed";
        return errorResult(new Error(errorMessage));
    }

    const validatedDataResult = mediaLibraryDirentResponseZodValidator.safeParse(response.data);
    if (validatedDataResult.success === false) {
        return errorResult(new Error("Error communicating with backend!"));
    }

    return dataResult(validatedDataResult.data);
}

export function uploadMediaLibraryFile({
    accessToken,
    file,
    onProgress,
    parentFolderId,
    intellsysBaseUrl,
    signal,
    workspaceId,
}: {
    accessToken: string;
    file: File;
    onProgress?: (progressPercent: number) => void;
    parentFolderId: Uuid;
    intellsysBaseUrl: string;
    signal?: AbortSignal;
    workspaceId: Uuid;
}): Promise<Result<MediaLibraryDirent>> {
    return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append("file", file);
        formData.append("parentFolderId", parentFolderId);

        const cleanupAbortListener = () => {
            if (signal !== undefined) {
                signal.removeEventListener("abort", handleAbort);
            }
        };

        const handleAbort = () => {
            xhr.abort();
        };

        if (signal !== undefined) {
            if (signal.aborted === true) {
                resolve(errorResult(new Error("Upload cancelled")));
                return;
            }

            signal.addEventListener("abort", handleAbort);
        }

        xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable === true && onProgress !== undefined) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        });

        xhr.addEventListener("load", () => {
            cleanupAbortListener();

            if (xhr.status < 200 || xhr.status >= 300) {
                resolve(errorResult(new Error(`Upload failed (${String(xhr.status)})`)));
                return;
            }

            try {
                const responseBody: unknown = JSON.parse(xhr.responseText);
                resolve(parseMediaLibraryUploadJsonResponse(responseBody));
            } catch {
                resolve(errorResult(new Error("Invalid response from server")));
            }
        });

        xhr.addEventListener("error", () => {
            cleanupAbortListener();
            resolve(errorResult(new Error("Network error during upload")));
        });

        xhr.addEventListener("abort", () => {
            cleanupAbortListener();
            resolve(errorResult(new Error("Upload cancelled")));
        });

        xhr.open("POST", `${intellsysBaseUrl}/sub-system/pluto/media-library/upload-file`);
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
        xhr.setRequestHeader("X-Workspace-Id", workspaceId);
        xhr.send(formData);
    });
}

export async function uploadMediaLibraryImageAndGetId({
    accessToken,
    file,
    parentFolderId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    file: File;
    parentFolderId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<Uuid>> {
    const uploadResult = await uploadMediaLibraryFile({
        accessToken,
        file,
        parentFolderId,
        intellsysBaseUrl,
        workspaceId,
    });
    if (uploadResult.success !== true) {
        return uploadResult;
    }

    return dataResult(uploadResult.data.id);
}

export async function purgeMediaLibraryCdnCache({
    accessToken,
    direntId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<{failed: boolean; urls: Array<string>}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {direntId: direntId},
        path: "/media-library/purge-cdn-cache",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            failed: zod.boolean(),
            urls: zod.array(zod.string()),
        }),
        workspaceId: workspaceId,
    });
}

export async function purgeMediaLibraryCdnCacheFolder({
    accessToken,
    folderDirentId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    folderDirentId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<{failed: boolean; purgedCount: number; urls: Array<string>}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {folderDirentId: folderDirentId},
        path: "/media-library/purge-cdn-cache-folder",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            failed: zod.boolean(),
            purgedCount: zod.number(),
            urls: zod.array(zod.string()),
        }),
        workspaceId: workspaceId,
    });
}

export async function purgeMediaLibraryCdnCacheByPath({
    accessToken,
    matchType,
    pathOrRegex,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    matchType: "exactPrefix" | "regex";
    pathOrRegex: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<{failed: boolean; matchedCount: number; target: string}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {
            matchType: matchType,
            pathOrRegex: pathOrRegex,
        },
        path: "/media-library/purge-cdn-cache-by-path",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            failed: zod.boolean(),
            matchedCount: zod.number(),
            target: zod.string(),
        }),
        workspaceId: workspaceId,
    });
}

export async function getMediaLibraryCdnUsageStats({
    accessToken,
    direntId,
    folderDirentId,
    pathPrefix,
    period,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId?: Uuid;
    folderDirentId?: Uuid;
    pathPrefix?: string;
    period?: "24h" | "7d";
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<{
    bandwidthBytes: number;
    hitRatio: number;
    hits: number;
    misses: number;
    period: "24h" | "7d";
    periodSeconds: number;
    requests: number;
    scope: "service";
    urlPrefix: string;
}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {
            direntId: direntId,
            folderDirentId: folderDirentId,
            pathPrefix: pathPrefix,
            period: period,
        },
        path: "/media-library/cdn-usage-stats",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            bandwidthBytes: zod.number(),
            hitRatio: zod.number(),
            hits: zod.number(),
            misses: zod.number(),
            period: zod.enum(["24h", "7d"]),
            periodSeconds: zod.number(),
            requests: zod.number(),
            scope: zod.literal("service"),
            urlPrefix: zod.string(),
        }),
        workspaceId: workspaceId,
    });
}

export async function getMediaLibraryDirentCdnState({
    accessToken,
    direntId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    direntId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<{
    events: Array<{
        createdAt: string;
        status: "completed" | "failed" | "requested";
        trigger: string | null;
        urls: Array<string>;
    }>;
    urls: Array<{
        lastPurgedAt: string | null;
        status: "completed" | "failed" | "never" | "requested";
        url: string;
    }>;
}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {direntId: direntId},
        path: "/media-library/dirent-cdn-state",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            events: zod.array(zod.object({
                createdAt: zod.string(),
                status: zod.enum(["completed", "failed", "requested"]),
                trigger: zod.string().nullable(),
                urls: zod.array(zod.string()),
            })),
            urls: zod.array(zod.object({
                lastPurgedAt: zod.string().nullable(),
                status: zod.enum(["completed", "failed", "never", "requested"]),
                url: zod.string(),
            })),
        }),
        workspaceId: workspaceId,
    });
}

export async function convertMediaLibraryDirentFormat({
    accessToken,
    altText,
    crop,
    description,
    direntId,
    fileName,
    intellsysBaseUrl,
    quality,
    replaceInPlace,
    targetFormat,
    workspaceId,
}: {
    accessToken: string;
    altText?: string;
    crop?: {
        height: number;
        left: number;
        top: number;
        width: number;
    };
    description?: string;
    direntId: Uuid;
    fileName?: string;
    intellsysBaseUrl: string;
    quality?: "high" | "low" | "medium";
    replaceInPlace?: boolean;
    targetFormat: "jpeg" | "png" | "webp";
    workspaceId: Uuid;
}): Promise<Result<{
    dirent: MediaLibraryDirent;
    purgedUrls: Array<string>;
    sourceDirentId: Uuid;
}>> {
    return plutoPost({
        accessToken: accessToken,
        body: {
            altText: altText,
            crop: crop,
            description: description,
            direntId: direntId,
            fileName: fileName,
            quality: quality,
            replaceInPlace: replaceInPlace,
            targetFormat: targetFormat,
        },
        path: "/media-library/convert-dirent-format",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.object({
            dirent: mediaLibraryDirentResponseZodValidator,
            purgedUrls: zod.array(zod.string()),
            sourceDirentId: uuidZodValidator,
        }),
        workspaceId: workspaceId,
    });
}
